import { UserRoles } from '@dynamika-shared/types/auth';
import { WithUserRolesHoc } from '@components/HOC/WithUserRoles/WithUserRoles.hoc';
import { SiteUserLayout } from '@components/SiteUserLayout/SiteUserLayout';
import { Layout } from '@components/Layout/Layout';
import { AuditTrialsList } from '../pages/AuditTrial/AuditTrialsList';
import { routesPath } from './routes';
import { SelectClinicalTrialForRole } from '@components/LoginSelections/SelectClinicalTrialForRole';
import { lazy, Suspense } from 'react';
import { PageLoading } from '@dynamika-shared/ui/PageLoading/PageLoading';
import { SponsorsMenu } from '@components/Sidebar/Menu/SponsorsMenu/SponsorsMenu';
import { LocalhostScoringForm } from 'src/pages/LocalhostScoringForm/LocalhostScoringForm';

const Sponsor = lazy(() => import('src/pages/Sponsor/Sponsor'));
const SponsorOverview = lazy(
  () => import('src/pages/Sponsor/SponsorOverview/SponsorOverview')
);
const SponsorEligibility = lazy(
  () => import('src/pages/Sponsor/SponsorEligibility/SponsorEligibility')
);
const SponsorStatistics = lazy(
  () => import('src/pages/Sponsor/SponsorStatistics/SponsorStatistics')
);
const ReadingCompleted = lazy(
  () => import('src/pages/Sponsor/ReadingCompleted/ReadingCompleted')
);
const ReadingTaskViewerPage = lazy(
  () => import('src/pages/Readings/ReadingTaskPage/ReadingTaskViewerPage')
);
const ReaderPlainForm = lazy(
  () => import('src/pages/Readings/ReadingTaskPage/ReaderPlainForm')
);
const ReadingTaskViewerPageDetached = lazy(
  () =>
    import('src/pages/Readings/ReadingTaskPage/ReadingTaskViewerPageDetached')
);
const OverviewUploadsView = lazy(
  () => import('src/pages/OverviewUploadsView/OverviewUploadsView')
);
const SponsorQueries = lazy(
  () => import('src/pages/Sponsor/SponsorQueries/SponsorQueries')
);
const SponsorQueryPage = lazy(
  () => import('src/pages/Sponsor/SponsorQueries/SponsorQueryPage')
);

const SimpleViewer = lazy(() => import('src/pages/SimpleViewer/SimpleViewer'));

export const sponsorsRoutes = [
  {
    path: routesPath.sponsor,
    element: (
      <WithUserRolesHoc allowedRoles={[UserRoles.SPONSOR]}>
        <SiteUserLayout>
          <SelectClinicalTrialForRole />
        </SiteUserLayout>
      </WithUserRolesHoc>
    ),
  },
  {
    path: routesPath.sponsorPages,
    element: (
      <WithUserRolesHoc allowedRoles={[UserRoles.SPONSOR]}>
        <Layout menuComponent={SponsorsMenu}>
          <Suspense fallback={<PageLoading className='h-full' />}>
            <Sponsor />
          </Suspense>
        </Layout>
      </WithUserRolesHoc>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageLoading className='h-full' />}>
            <SponsorOverview />
          </Suspense>
        ),
      },
      {
        path: 'overview',
        element: (
          <Suspense fallback={<PageLoading className='h-full' />}>
            <SponsorOverview />
          </Suspense>
        ),
      },
      {
        path: 'eligibility',
        element: (
          <Suspense fallback={<PageLoading className='h-full' />}>
            <SponsorEligibility />
          </Suspense>
        ),
      },
      {
        path: 'statistics',
        element: (
          <Suspense fallback={<PageLoading className='h-full' />}>
            <SponsorStatistics />
          </Suspense>
        ),
      },
      {
        path: 'readings-completed',
        element: (
          <Suspense fallback={<PageLoading className='h-full' />}>
            <ReadingCompleted />
          </Suspense>
        ),
      },
      {
        title: 'Uploads',
        path: 'uploads',
        element: (
          <Suspense fallback={<PageLoading className='h-full' />}>
            <OverviewUploadsView  />
          </Suspense>
        ),
      },
      {
        path: 'queries',
        element: (
          <Suspense fallback={<PageLoading className='h-full' />}>
            <SponsorQueries />
          </Suspense>
        ),
      },
      {
        path: 'queries/:queryId',
        element: (
          <Suspense fallback={<PageLoading className='h-full' />}>
            <SponsorQueryPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: routesPath.sponsorsAuditClinicalTrial,
    element: (
      <WithUserRolesHoc allowedRoles={[UserRoles.SPONSOR]}>
        <Layout>
          <AuditTrialsList />
        </Layout>
      </WithUserRolesHoc>
    ),
  },
  {
    path: routesPath.localhostScoringForm,
    element: (
      <WithUserRolesHoc allowedRoles={[UserRoles.ADMIN]}>
        <LocalhostScoringForm />
      </WithUserRolesHoc>
    ),
  },
  {
    path: routesPath.sponsorTaskViewer,
    element: (
      <WithUserRolesHoc allowedRoles={[UserRoles.SPONSOR]}>
        <Suspense fallback={<PageLoading className='h-full' />}>
          <ReadingTaskViewerPage />
        </Suspense>
      </WithUserRolesHoc>
    ),
  },
  {
    path: routesPath.sponsorTaskSimpleViewer,
    element: (
      <WithUserRolesHoc allowedRoles={[UserRoles.SPONSOR]}>
        <Suspense fallback={<PageLoading className='h-full' />}>
          <SimpleViewer />
        </Suspense>
      </WithUserRolesHoc>
    ),
  },
  {
    path: routesPath.sponsorSubTaskViewer,
    element: (
      <WithUserRolesHoc allowedRoles={[UserRoles.SPONSOR]}>
        <Suspense fallback={<PageLoading className='h-full' />}>
          <ReadingTaskViewerPage />
        </Suspense>
      </WithUserRolesHoc>
    ),
  },
  {
    path: routesPath.sponsorTaskPlain,
    element: (
      <WithUserRolesHoc allowedRoles={[UserRoles.SPONSOR]}>
        <Suspense fallback={<PageLoading className='h-full' />}>
          <ReaderPlainForm />
        </Suspense>
      </WithUserRolesHoc>
    ),
  },
  {
    path: routesPath.sponsorTaskViewerDetached,
    element: (
      <WithUserRolesHoc allowedRoles={[UserRoles.SPONSOR]}>
        <Suspense fallback={<PageLoading className='h-full' />}>
          <ReadingTaskViewerPageDetached />
        </Suspense>
      </WithUserRolesHoc>
    ),
  },
  {
    path: routesPath.sponsorSubTaskViewerDetached,
    element: (
      <WithUserRolesHoc allowedRoles={[UserRoles.SPONSOR]}>
        <Suspense fallback={<PageLoading className='h-full' />}>
          <ReadingTaskViewerPageDetached />
        </Suspense>
      </WithUserRolesHoc>
    ),
  },
];
